import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../../Components/UI/dialog';
import { Button } from '../../../Components/UI/button';
import { X, Check, ArrowLeft, ChevronRight, LogOut } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../../../Components/UI/tabs';
import { useCurrency } from '../../../Hooks/useCurrency';
import { useDesktop } from '../../../Components/Common/IsDesktop';
import { FormatAmount, isIOSDevice, openInExternalBrowser, createRazorpayOptions, getSubscriptionPlans, getCoupons, getDateWithoutTime, formatDateFull, freemiumEditionID, getAt99 } from '../../../Utils/Utilities';
import { countries } from '../../../Components/Enums/DataEnums';
import { createRazorpayOrderAPI, lastUpgradeAttempt, upgradeSubscriptionAPI } from './UpgradeSubscriptionAPI';
import { useSelector } from 'react-redux';
import { getAgencyDetails, getUsersArr } from '../../../Store/Reducers';
import { toast } from 'sonner';
import ProfileSection from '../Profile/ProfileSection';
import StartSubscriptionDialog from './StartSubscriptionDialog';
import { useSubscriptionModalState } from '../../../Hooks/useSubscriptionModalState';
import AvailableCoupons from './AvailableCoupons';
import apiService from '../../../Services/APIService';
import { getAuthData } from '../../../Utils/AuthUtils';
import { clearEverything } from '../../../Utils/CookieHandler';

const UpgradeSubscription = ({
	open,
	onClose,
	hideCloseButton = false,
	onPaymentStart = () => { },
	onPaymentCancel = () => { },
	onPaymentSuccess = () => { }
}) => {
	const isDesktop = useDesktop();
	const { setUpgradeModalOpen } = useSubscriptionModalState();
	const agencyData = useSelector(getAgencyDetails);
	const userData = useSelector(getUsersArr);
	const { EditionID: currEdnTID = 1, SubMode: subsType = '', CountryID = 1, SubTypeID: currSubTypeID = 1 } = agencyData ?? {};
	const baseCurrencySymbol = useCurrency();
	const isIndianUser = CountryID === 1;
	const isFreemiumAccount = currEdnTID === freemiumEditionID;
	const showMM = (agencyData?.SubTypeID > 1 && agencyData?.SubMode === 'MM' && agencyData?.SubType === 'Premium');
	const showYY = true;
	const [selectedPlan, setSelectedPlan] = useState(showYY ? 'yearly' : 'monthly');
	const nowDT = new Date();
	const isPeakSell = false;
	const currencySymbol = isIndianUser ? baseCurrencySymbol : '$';
	const subscriptionPlans = getSubscriptionPlans(CountryID);

	const getFilteredPlans = (plans, currentEditionID, agencyData) => {
		const isFreeAccount = currentEditionID === 1 || (agencyData?.SubTypeID <= 1 && (!agencyData?.SubType || agencyData?.SubType?.toLowerCase().includes('free')));

		if (isFreeAccount) {
			// For free accounts, only show Edition 7 (Smart Management)
			return plans.filter(p => p.editionID === 7);
		}

		if (currentEditionID === 7) {
			// For Smart Management (Edition 7) subscribers, ONLY show Edition 7!
			return plans.filter(p => p.editionID === 7);
		}

		// For other paid accounts (currentEditionID > 1):
		// Hide Edition 7
		const currentPlan = plans.find(p => p.editionID === currentEditionID);

		return plans.filter(plan => {
			if (plan.editionID === 7 && currentEditionID !== 7) {
				return false;
			}

			if (!currentPlan) {
				return true;
			}

			const currentMaxUsers = currentPlan.cntMaxUsers;
			const currentMaxConns = currentPlan.cntMaxConns;
			const planMaxUsers = plan.cntMaxUsers;
			const planMaxConns = plan.cntMaxConns;

			const meetsUserRequirement =
				currentMaxUsers === -1 ?
					planMaxUsers === -1 :
					(planMaxUsers === -1 || planMaxUsers >= currentMaxUsers);

			const meetsConnRequirement = planMaxConns === -1 || planMaxConns >= currentMaxConns;

			return meetsUserRequirement && meetsConnRequirement;
		});
	};

	const filteredPlans = getFilteredPlans(subscriptionPlans, currEdnTID, agencyData).filter(plan => {
							const isCurrentPlan = currEdnTID > 1 ? plan.editionID === currEdnTID : false;
							const isRenewalScenario = isCurrentPlan && plan.editionID > 1 && ((selectedPlan === 'yearly' && subsType === 'YY') || (selectedPlan === 'monthly' && subsType === 'MM'));
							return (plan?.show || isRenewalScenario || isCurrentPlan) && (plan.monthly || plan.yearly);
						  });

	const btnTexts = {
		upgrade: isDesktop ? 'Upgrade Now' : 'Upgrade',
		start: isDesktop ? 'Start Now' : 'Start',
		renew: 'Renew'
	}
	const [showProfileDialog, setShowProfileDialog] = useState(false);
	const [pendingPlan, setPendingPlan] = useState(null);
	const [showStartSubscriptionDialog, setShowStartSubscriptionDialog] = useState(false);
	const [selectedPlanForDialog, setSelectedPlanForDialog] = useState(null);
	const [rfdAvl, setRfdAvl] = useState(false);
	const [pendingBillingPeriod, setPendingBillingPeriod] = useState('yearly');
	const [showCouponModal, setShowCouponModal] = useState(false);
	const [couponPlan, setCouponPlan] = useState(null);
	const [couponBillingPeriod, setCouponBillingPeriod] = useState('yearly');
	const [couponChangeType, setCouponChangeType] = useState('');
	let changeType = "";

	// Clear AppliedCoupon from localStorage when modal opens
	useEffect(() => {
		if (open) {
			localStorage.removeItem('AppliedCoupon');
			if (currEdnTID === 1) {
				setSelectedPlan('monthly');
			} else if (showYY) {
				setSelectedPlan('yearly');
			} else if (showMM) {
				setSelectedPlan('monthly');
			}
		}
		setUpgradeModalOpen(open);
	}, [open, showMM, showYY, currEdnTID, setUpgradeModalOpen]);

	useEffect(() => {
		const scriptID = "razorpay-checkout-js";
		const existingScript = document.getElementById(scriptID);

		if (!existingScript) {
			const script = document.createElement("script");
			script.id = scriptID;
			script.src = "https://checkout.razorpay.com/v1/checkout.js";
			script.async = true;
			document.body.appendChild(script);
		}

		return () => { };
	}, []);

	const handleClose = () => {
		onClose();
	};

	const handleOpenChange = (isOpen) => {
		if (!isOpen) {
			onClose();
		}
	};

	const handleLogout = async () => {
		try {
			const authData = getAuthData();

			if (!authData) {
				throw new Error('No authentication data found');
			}

			const result = await apiService.accessSetData(2, 'logout', {});
			const parsedResult = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;

			if (parsedResult?.Table && parsedResult.Table[0]?.FnErrCode !== '-1') {
				throw new Error(parsedResult.Table[0]?.ErrMsg || 'Logout failed');
			}
			clearEverything();

			return {
				success: true,
				message: 'Logged out'
			};
		} catch (error) {
			console.error('Error logging out:', error);
			toast.error(error.message || 'Failed to logout');
			throw error;
		}
	};

	const handleProfileDialogClose = () => {
		setShowProfileDialog(false);
	};

	const handleStartSubscriptionClose = () => {
		setShowStartSubscriptionDialog(false);
		setSelectedPlanForDialog(null);
	};

	const handleProfileSaveSuccess = (data) => {
		setShowProfileDialog(false);
		if (pendingPlan) {
			setSelectedPlan(pendingBillingPeriod);
			setSelectedPlanForDialog(pendingPlan);
			setShowStartSubscriptionDialog(true);
			handleClose();
		}
	};

	const handleCouponSelect = (coupon) => {
		// Store selected coupon in localStorage
		localStorage.setItem('AppliedCoupon', JSON.stringify(coupon));
		setShowCouponModal(false);

		// Proceed with payment flow
		if (couponPlan) {
			handlePayment(couponPlan, couponBillingPeriod);
		}
	};

	const handleShowCoupons = (plan, billingPeriod, changeTypeVal) => {
		setCouponPlan(plan);
		setCouponBillingPeriod(billingPeriod);
		setCouponChangeType(changeTypeVal); // Store changeType in state
		setShowCouponModal(true);
	};

	const handlePayment = async (plan, billingPeriod) => {
		const effectivePeriod = (!plan?.yearly && plan?.monthly) ? 'monthly' : billingPeriod;
		const planPricing = effectivePeriod === 'yearly' ? plan?.yearly : plan?.monthly;

		if (!planPricing) {
			toast.error('Pricing information is not available.');
			return;
		}

		const gstin = agencyData?.TaxID;
		const city = agencyData?.City;
		const state = agencyData?.State;
		const agencyName = agencyData?.AgencyName;
		// const isMissingAgencyDetails = !agencyName || agencyName.trim() === '' || !city || city.trim() === '' || !state || state.trim() === '' || !gstin || gstin.trim() === '';
		const isMissingAgencyDetails = false;

		if (isMissingAgencyDetails) {
			setPendingPlan(plan);
			setPendingBillingPeriod(effectivePeriod);
			setShowProfileDialog(true);
			return;
		}

		setSelectedPlanForDialog(plan);
		setPendingPlan(plan);
		setPendingBillingPeriod(effectivePeriod);
		setRfdAvl(plan?.rfdAvl || false);
		setShowStartSubscriptionDialog(true);
		handleClose();
	};

	const handleStartSubscriptionPayment = async () => {
		if (!pendingPlan) {
			toast.error('Plan information is missing.');
			return;
		}
		await handlePaymentProcess(pendingPlan, pendingBillingPeriod);
	};

	const handlePaymentProcess = async (plan, billingPeriod) => {
		const effectivePeriod = (!plan?.yearly && plan?.monthly) ? 'monthly' : billingPeriod;
		const planPricing = effectivePeriod === 'yearly' ? plan?.yearly : plan?.monthly;
		const planMode = effectivePeriod === 'yearly' ? 'YY' : 'MM';
		const upgradeLink = planPricing?.link;

		if (!plan?.editionID) {
			toast.error('Plan information is invalid. Please try again.');
			return;
		}

		if (!planMode || (planMode !== 'YY' && planMode !== 'MM')) {
			toast.error('Subscription mode is invalid. Please try again.');
			return;
		}

		if (!agencyData?.CountryID || !agencyData?.Country) {
			toast.error('Country information is missing. Please update your profile.');
			return;
		}

		await lastUpgradeAttempt();

		if (upgradeLink) {
			setShowStartSubscriptionDialog(false);
			handleClose();
			openInExternalBrowser(upgradeLink);
			toast.success('Redirecting to payment gateway...');
			return;
		}

		if (!window.Razorpay) {
			toast.error('Payment gateway not loaded. Please refresh and try again.');
			return;
		}

		try {
			const handleRazorpaySuccess = async (response) => {
				try {
					const countryObj = countries.find(c => c.CountryID === Number(agencyData?.CountryID || 1));
					const currencyID = agencyData?.CurrencyID || countryObj?.CurrencyID || (isIndianUser ? 24 : 50);
					const currencyCode = isIndianUser ? 'INR' : 'USD';

					const upgradeData = {
						editionID: plan?.editionID,
						subMode: planMode,
						changeType: isNewSubscription() ? "SUB" : changeType,
						countryID: agencyData?.CountryID || 1,
						currencyID: currencyID,
						CurrencyID: currencyID,
						currencyCode: currencyCode,
						currencySymbol: currencySymbol,
					};

					const result = await upgradeSubscriptionAPI(upgradeData);
					if (!result.error) {
						toast.success("Your account is upgraded.");
						setShowStartSubscriptionDialog(false);
						handleClose();
					} else {
						toast.error("Failed to Upgrade subscription", { description: result.message });
					}
				} catch (error) {
					console.error(`Upgrade subscription request API error:`, error);
					toast.error('Error processing upgrade. Please try again.');
				}
			};

			const handleRazorpayDismiss = () => {
				toast.info('Payment cancelled.');
			};

			const orderResponse = await createRazorpayOrderAPI({
				amount: planPricing.price
			});

			if (orderResponse.error) {
				toast.error(orderResponse.message || 'Failed to create order. Please try again.');
				return;
			}

			const orderDataset = JSON.parse(orderResponse?.updatePackage || '[]');
			if (orderDataset.length == 0) {
				toast.error(orderDataset.message || 'Failed to create order. Please try again.');
				return;
			}
			const orderInfo = orderDataset.Table1[0];

			const options = createRazorpayOptions(
				agencyData,
				userData,
				handleRazorpaySuccess,
				handleRazorpayDismiss,
				orderInfo
			);

			if (!options) {
				toast.error('Could not prepare payment. Please try again.');
				return;
			}

			const razorpay = new window.Razorpay(options);
			razorpay.open();
		} catch (error) {
			console.error('Payment initiation error:', error);
			toast.error('Error initiating payment. Please try again.');
		}
	};

	// Helper function to determine if agency is a new subscription or has upgrade history
	const isNewSubscription = () => (agencyData?.SubTypeID === 1 || agencyData?.SubTypeID === 0) && !agencyData?.LastUpgdDT;

	const toggleButtonSaving = {
		month: 0,
		year: 0
	};

	const handleUpgradeClick = async (plan, billingPeriod) => {
		await handlePayment(plan, billingPeriod);
	};

	const handleRZPayUpgradeClick = async (plan, updatedProfileData = null) => {
		await handlePayment(plan, selectedPlan);
		return;
	};

	const getIconColor = (planID) => {
		switch (planID) {
			case 'free':
			case 'smartSeller':
				return 'bg-gray-100 text-gray-600';
			case 'pro':
			case 'smartTeam':
				return 'bg-sahi-orange-light';
			case 'corporate':
			case 'smartBusiness':
				return 'bg-sahi-blue-light text-sahi-blue';
			default:
				return '';
		}
	};

	const renderPlanCard = (plan) => {
		const effectivePeriod = (!plan?.yearly && plan?.monthly) ? 'monthly' : selectedPlan;
		const planPricing = effectivePeriod === 'yearly' ? plan?.yearly : plan?.monthly;
		if (!planPricing) return null;

		const isCurrentPlan = plan.editionID === currEdnTID;
		const isHigherTier = plan.editionID > currEdnTID;
		const currentSubTypeID = agencyData?.SubTypeID;
		const expiryDT = agencyData?.SubTillDT;

		let buttonText = '';
		let showButton = false;

		if (isCurrentPlan && currEdnTID > 1) {
			// Current plan — check if it's a renewal or period switch
			const samePeriod = (selectedPlan === 'yearly' && subsType === 'YY') || (selectedPlan === 'monthly' && subsType === 'MM');

			if (samePeriod) {
				buttonText = 'Renew';
				changeType = "RNWL";
				showButton = true;
			} else {
				if (subsType === 'YY' && selectedPlan === 'monthly') {
					showButton = false;
				} else {
					buttonText = 'Upgrade Now';
					changeType = "UPGD";
					showButton = true;
				}
			}
		}
		else if (currentSubTypeID === 1) {
			buttonText = 'Start Now';
			changeType = "SUB";
			showButton = true;
		}
		else if (isNewSubscription()) {
			buttonText = 'Start Now';
			changeType = "SUB";
			showButton = true;
		}
		else if (isHigherTier) {
			if (isCurrentPlan === 3 && subsType === 'YY') {
				showButton = false;
			} else {
				buttonText = 'Upgrade Now';
				changeType = "UPGD";
				showButton = true;
			}
		}

		const isRenewal = isCurrentPlan && ((selectedPlan === 'yearly' && subsType === 'YY') || (selectedPlan === 'monthly' && subsType === 'MM'));
		let displayPrice;

		if (isRenewal) {
			const renewalRate = selectedPlan === 'yearly' ? agencyData?.AnnRnwlRate : agencyData?.MthRnwlRate;
			if (renewalRate !== undefined && renewalRate !== null && renewalRate !== '' && renewalRate !== 0) {
				displayPrice = String(renewalRate);
			}
		}

		if (!displayPrice) {
			displayPrice = (isPeakSell && selectedPlan === 'yearly' && isIndianUser && planPricing.originalPrice != 0) ? planPricing.originalPrice : planPricing.price;
		}

		// Freemium plan always shows ₹0
		const isFreemiumPlan = plan.editionID === freemiumEditionID;
		if (isFreemiumPlan) displayPrice = '0';

		return (
			<div
				key={plan.id}
				className={`sahi-card relative my-4 overflow-hidden transition-all duration-300 justify-center hover:shadow-lg lg:min-w-0 w-[300px] sm:m-1 ${plan.popular ? 'ring-2 ring-sahi-blue' : ''
					}`}
				style={{ height: 'auto' }}
				onClick={() => {
					if (plan.id === "free" || !showButton || (isFreemiumPlan && isFreemiumAccount)) return;
					handleUpgradeClick(plan, selectedPlan);
				}}
			>
				{isCurrentPlan && currEdnTID > 1 && agencyData?.SubTillDT && (
					<div className="absolute top-0 right-0 bg-sahi-primary-color text-white text-[10px] px-3 py-1 sahi-radius-bl font-bold z-10 tracking-wide">
						{agencyData?.SubTillDT && 'Current Plan'}
					</div>
				)}
				<div className="pb-4 h-full flex flex-col">
					<div className="text-center mb-4">
						{isDesktop && (
							<div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${getIconColor(plan.id)}`}>
								{plan.icon}
							</div>
						)}
						<div className="flex justify-center">
							<h3 className="text-xl font-semibold text-gray-900 mb-1">{plan.name}</h3>
							{plan.id !== "free" && (
								<Button
									onClick={(e) => {
										e.stopPropagation();
										handlePayment(plan, selectedPlan);
									}}
									variant="ghost"
									size="sm"
									className="text-xs cursor-default"
								>
								</Button>
							)}
						</div>
						<p className="text-sm text-gray-600 flex-1">{plan.description}</p>
					</div>

					<div className="text-center mb-3">
						{!isFreemiumPlan && selectedPlan === 'yearly' && plan.yearly && plan.yearly.discount !== 0 && (
							<div className="flex items-center justify-center gap-2 mb-2">
								<span className="text-sm text-gray-500 line-through flex">
									{currencySymbol}
									<FormatAmount
										amount={planPricing.originalPrice}
										currency={currencySymbol}
										showCurr={false}
									/> / year
								</span>
								<span className="sahi-badge-green text-xs font-semibold">
									{plan.yearly.discount}% OFF
								</span>
							</div>
						)}
						<div className={"mb-2 flex items-end justify-center pb-0" + (!isFreemiumPlan && selectedPlan === 'yearly' && plan.yearly && plan.yearly.discount !== 0 ? '' : ' mt-8')}>
							{isFreemiumPlan ? (
								<span className="text-3xl font-bold text-gray-900 leading-none">Free</span>
							) : (
								<>
									<span className="text-2xl text-gray-600 leading-none mr-2 mb-0.5">{currencySymbol}</span>
									<FormatAmount
										amount={displayPrice}
										currency={currencySymbol}
										showCurr={false}
										amtClassName="text-3xl font-bold text-gray-900 leading-none"
									/>
									<span className="text-gray-600 ml-2 leading-none mb-1">{planPricing.period}</span>
								</>
							)}
						</div>
					</div>

					<div className="flex-1 mb-6">
						<ul className="space-y-3">
							{plan.features.map((feature, index) => (
								<li key={index} className="flex items-center justify-center gap-3">
									<Check className="w-4 h-4 text-sahi-green flex-shrink-0" />
									<span className="text-sm text-gray-700">{feature}</span>
								</li>
							))}
						</ul>
					</div>

					{isFreemiumPlan && isFreemiumAccount ? (
						<Button
							disabled
							className="w-full py-3 font-semibold !rounded-lg opacity-60 cursor-not-allowed"
						>
							Currently Active
						</Button>
					) : showButton && (
						<Button
							onClick={(e) => {
								e.stopPropagation();
								handlePayment(plan, selectedPlan);
							}}
							className={`w-full py-3 font-semibold transition-all duration-200 sahi-primary-button ${showYY && !showMM && selectedPlan === 'yearly' && getCoupons(plan.editionID, 'yearly', changeType).filter(c => getAt99 || c.proAmt > 99).length > 0 ? '!rounded-none !rounded-t-lg' : '!rounded-none !rounded-lg'}`}
						>
							{buttonText}
						</Button>
					)}

					{showYY && !showMM && selectedPlan === 'yearly' && getCoupons(plan.editionID, 'yearly', changeType).filter(c => getAt99 || c.proAmt > 99).length > 0 && (
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleShowCoupons(plan, 'yearly', currSubTypeID === 1 ? "SUB" : isNewSubscription() ? "SUB" : isCurrentPlan && subsType === 'YY' ? "RNWL" : "UPGD");
							}}
							className={`w-full border border-t-0 bg-sahi-green-light text-gray-900 font-semibold px-3 py-2 rounded-b-lg flex items-center justify-between`}
						>
							<span className="text-sm flex items-center gap-2">
								<span>Offers Available</span>
								<span className='font-medium'>({getCoupons(plan.editionID, 'yearly', currSubTypeID === 1 ? "SUB" : isNewSubscription() ? "SUB" : isCurrentPlan && subsType === 'YY' ? "RNWL" : "UPGD").length})</span>
							</span>
							<ChevronRight size={16} />
						</button>
					)}
				</div>
			</div>
		);
	};

	const renderPricingCard = (plan) => {
		const yearlyPricing = plan.yearly;
		const monthlyPricing = plan.monthly;

		if (!yearlyPricing && !monthlyPricing) return null;

		const isCurrentPlan = plan.editionID === currEdnTID;
		const isHigherTier = plan.editionID > currEdnTID;
		const isFreemiumPlan = plan.editionID === freemiumEditionID;
		// Format the expiry date from agency details
		const expiryDT = agencyData?.SubTillDT;
		const getBtnState = (billingPeriod) => {
			let buttonText = '';
			let showButton = false;

			if (isCurrentPlan && currEdnTID > 1) {
				// Current plan — check period match before falling through to new-sub logic
				const isCurrentPeriod = (billingPeriod === 'yearly' && subsType === 'YY') || (billingPeriod === 'monthly' && subsType === 'MM');

				if (isCurrentPeriod) {
					buttonText = btnTexts.renew;
					changeType = "RNWL";
					showButton = true;
				} else {
					if (subsType === 'YY' && billingPeriod === 'monthly') {
						showButton = false;
					} else {
						buttonText = btnTexts.upgrade;
						changeType = "UPGD";
						showButton = true;
					}
				}
			} else if (currEdnTID === 1) {
				buttonText = btnTexts.start;
				changeType = "SUB";
				showButton = true;
			} else if (isNewSubscription()) {
				buttonText = btnTexts.start;
				changeType = "SUB";
				showButton = true;
			} else if (isHigherTier) {
				if (subsType === 'YY' && billingPeriod === 'monthly') {
					showButton = false;
				} else {
					buttonText = btnTexts.upgrade;
					changeType = "UPGD";
					showButton = true;
				}
			}

			return { buttonText, showButton };
		};

		const yearlyButton = getBtnState('yearly');
		const monthlyButton = getBtnState('monthly');

		const isRenewalYearly = isCurrentPlan && subsType === 'YY';
		const isRenewalMonthly = isCurrentPlan && subsType === 'MM';

		const renderPriceSection = (pricing, billingPeriod) => {
			if (!pricing) return null;

			const isRenewalYearlyBtn = yearlyButton.showButton && yearlyButton.buttonText === btnTexts.renew && billingPeriod === 'yearly';
			const isRenewalMonthlyBtn = monthlyButton.showButton && monthlyButton.buttonText === btnTexts.renew && billingPeriod === 'monthly';
			const isRenewal = (billingPeriod === 'yearly' && isRenewalYearlyBtn) || (billingPeriod === 'monthly' && isRenewalMonthlyBtn);

			let displayPrice;
			if (isRenewal && isCurrentPlan) {
				const renewalRate = billingPeriod === 'yearly' ? agencyData?.AnnRnwlRate : agencyData?.MthRnwlRate;
				if (renewalRate !== undefined && renewalRate !== null && renewalRate !== '' && renewalRate !== 0) {
					displayPrice = String(renewalRate);
				}
			}

			if (!displayPrice) {
				displayPrice = isPeakSell && billingPeriod === 'yearly' && pricing.originalPrice ? pricing.originalPrice : pricing.price;
			}

			// Freemium plan always shows ₹0
			if (plan.editionID === freemiumEditionID) displayPrice = '0';

			const hasDiscount = pricing.originalPrice && pricing.price && pricing.originalPrice !== pricing.price && pricing.originalPrice !== 0;
			const periodText = billingPeriod === 'yearly' ? '/ year*' : '/ month*';

			return (
				<div className="flex flex-col gap-1 w-full">
					{!isFreemiumPlan && hasDiscount && !isPeakSell && (
						<div className="flex items-center justify-center gap-2 w-full">
							<span className="text-sm text-gray-500 line-through flex items-baseline">
								{currencySymbol}
								<FormatAmount
									amount={pricing.originalPrice}
									currency={currencySymbol}
									showCurr={false}
								/>
							</span>
							{pricing.discount > 0 && (
								<span className="sahi-badge-green text-xs font-semibold px-2 py-0.5">
									{pricing.discount}% OFF
								</span>
							)}
						</div>
					)}

					<div className="flex items-baseline gap-1">
						{isFreemiumPlan ? (
							<span className="text-xl md:text-2xl font-bold text-gray-900">Free</span>
						) : (
							<div className="flex flex-row text-2xl font-bold">
								<div className='font-extralight flex items-end text-[16px] md:text-[18px] text-gray-600 mr-2 mb-[-3px]'>
									{currencySymbol}
								</div>
								<FormatAmount
									amount={displayPrice}
									currency={currencySymbol}
									showCurr={false}
									amtClassName='text-xl md:text-2xl'
								/>
							</div>
						)}
						{!isFreemiumPlan && <span className="text-sm text-gray-600 ml-1">{periodText}</span>}
					</div>
				</div>
			);
		};

		return (
			<div
				key={plan.id}
				className={`sahi-card relative my-4 overflow-hidden transition-all duration-300 justify-center hover:shadow-lg ${filteredPlans.length === 1 ? 'w-full max-w-md mx-auto sm:m-0' : 'lg:min-w-0 md:w-[380px] lg:w-[400px] sm:m-1'} ${plan.popular ? 'ring-2 ring-sahi-blue' : ''}`}
				style={{ height: 'auto' }}
			>
				{isCurrentPlan && currEdnTID > 1 && agencyData?.SubTillDT && (
					<div className="absolute top-0 right-0 bg-sahi-primary-color text-white text-[10px] px-3 py-1 sahi-radius-bl font-bold z-10 tracking-wide">
						{agencyData?.SubTillDT && 'Current Plan'}
					</div>
				)}


				<div className=" flex flex-col">
					<div className="text-center mb-4">
						{isDesktop && (
							<div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${getIconColor(plan.id)}`}>
								{plan.icon}
							</div>
						)}

						<div className="flex justify-center">
							<h3 className="text-xl font-semibold text-gray-900 mb-1">{plan.name}</h3>
							{plan.id !== "free" && (
								<Button
									variant="ghost"
									size="sm"
									className="text-xs cursor-default"
								>
								</Button>
							)}
						</div>

						<p className="text-sm text-gray-600">{plan.description}</p>
					</div>

					<div className="flex-1 mb-6 justify-center mx-auto">
						<ul className="flex flex-col space-y-1 w-full">
							{plan.features.map((feature, index) => (
								<li key={index} className="flex items-center justify-center gap-3">
									<Check className="w-4 h-4 text-sahi-green flex-shrink-0" />
									<span className="text-sm text-gray-700 text-center">{feature}</span>
								</li>
							))}
						</ul>
					</div>

					<div className="space-y-3 flex items-stretch flex-col h-full">
						{yearlyPricing && (
							<div className="space-y-0">
								<div
									className={`flex flex-col p-2 gap-2 ${plan.editionID == 2 ? 'py-4' : ''} bg-sahi-disable border ${getCoupons(plan.editionID, 'yearly', yearlyButton.showButton ? (currSubTypeID === 1 ? "SUB" : isNewSubscription() ? "SUB" : isCurrentPlan && subsType === 'YY' ? "RNWL" : "UPGD") : "").filter(c => getAt99 || c.proAmt > 99).length != 0 ? 'border-b-0' : (plan.editionID === 2 ? 'py-5' : 'py-3') + ' rounded-b-lg'} rounded-t-lg cursor-pointer`}
								>
									<div
										className="flex xs:flex-row items-center justify-between gap-2 bg-sahi-disable cursor-pointer"
										onClick={(e) => {
											if (yearlyButton.showButton && !(plan.editionID === freemiumEditionID && isFreemiumAccount)) {
												e.stopPropagation();
												handlePayment(plan, 'yearly');
											}
										}}
									>
										{renderPriceSection(yearlyPricing, 'yearly')}

										{plan.editionID === freemiumEditionID && isFreemiumAccount ? (
											<Button
												disabled
												className="min-w-[92px] md:min-w-[126px] opacity-60 cursor-not-allowed"
											>
												Currently Active
											</Button>
										) : yearlyButton.showButton && (
											<Button
												className="sahi-primary-button min-w-[92px] md:min-w-[126px]"
											>
												{yearlyButton.buttonText}
											</Button>
										)}
									</div>
								</div>

								{getCoupons(plan.editionID, 'yearly', yearlyButton.showButton ? (currSubTypeID === 1 ? "SUB" : isNewSubscription() ? "SUB" : isCurrentPlan && subsType === 'YY' ? "RNWL" : "UPGD") : "").filter(c => getAt99 || c.proAmt > 99).length != 0 ? (
									<button
										onClick={(e) => {
											e.stopPropagation();
											handleShowCoupons(plan, 'yearly', currSubTypeID === 1 ? "SUB" : isNewSubscription() ? "SUB" : isCurrentPlan && subsType === 'YY' ? "RNWL" : "UPGD");
										}}
										className="w-full border border-t-0 bg-sahi-green-light text-gray-900 font-semibold px-3 py-2 rounded-b-lg flex items-center justify-between"
									>
										<span className="text-xs flex items-center gap-2">
											<span>Offers Available</span>
											<span className='font-medium'>({getCoupons(plan.editionID, 'yearly', yearlyButton.showButton ? (currSubTypeID === 1 ? "SUB" : isNewSubscription() ? "SUB" : isCurrentPlan && subsType === 'YY' ? "RNWL" : "UPGD") : "").length})</span>
										</span>
										<ChevronRight size={16} />
									</button>
								) : null}
							</div>
						)}
						{monthlyPricing && (plan.editionID === currEdnTID || !yearlyPricing || plan.monthlyOnly) && (
							<div className="space-y-0">
								<div
									className={`flex flex-col p-2 gap-2 bg-sahi-disable border ${getCoupons(plan.editionID, 'monthly', monthlyButton.showButton ? (currEdnTID === 1 ? "SUB" : isCurrentPlan && subsType === 'MM' ? "RNWL" : "UPGD") : "").filter(c => getAt99 || c.proAmt > 99).length != 0 ? 'border-b-0' : 'py-3 rounded-b-lg'} rounded-t-lg cursor-pointer`}
								>
									<div
										className="flex xs:flex-row items-center justify-between gap-2 bg-sahi-disable cursor-pointer"
										onClick={(e) => {
											if (monthlyButton.showButton) {
												e.stopPropagation();
												handlePayment(plan, 'monthly');
											}
										}}
									>
										{renderPriceSection(monthlyPricing, 'monthly')}

										{monthlyButton.showButton && (
											<Button
												className="sahi-primary-button min-w-[92px] md:min-w-[126px]"
											>
												{monthlyButton.buttonText}
											</Button>
										)}

									</div>
								</div>

								{getCoupons(plan.editionID, 'monthly', monthlyButton.showButton ? (currSubTypeID === 1 ? "SUB" : isCurrentPlan && subsType === 'MM' ? "RNWL" : "UPGD") : "").filter(c => getAt99 || c.proAmt > 99).length != 0 ? (
									<button
										onClick={(e) => {
											e.stopPropagation();
											handleShowCoupons(plan, 'monthly', currSubTypeID === 1 ? "SUB" : isNewSubscription() ? "SUB" : isCurrentPlan && subsType === 'MM' ? "RNWL" : "UPGD");
										}}
										className="w-full border border-t-0 bg-sahi-green-light text-gray-900 font-semibold px-3 py-2 rounded-b-lg flex items-center justify-between"
									>
										<span className="text-xs flex items-center gap-2">
											<span>Offers Available</span>
											<span className='font-medium'>({getCoupons(plan.editionID, 'monthly', monthlyButton.showButton ? (currSubTypeID === 1 ? "SUB" : isCurrentPlan && subsType === 'MM' ? "RNWL" : "UPGD") : "").length})</span>
										</span>
										<ChevronRight size={16} />
									</button>
								) : null}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	//   const renderInternationalContent = () => (
	//     <div className="text-center py-8 px-6">
	//       <div className="max-w-md mx-auto">
	//         <div className="bg-sahi-blue-light rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
	//           <svg className="w-10 h-10 text-sahi-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
	//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
	//           </svg>
	//         </div>

	//         <p className="text-gray-600 mb-8 leading-relaxed">
	//           Thank you for your interest in SahiCRM!<br /> Please contact team SahiCRM to start your subscription.
	//         </p>

	//         <Button
	//           onClick={() => window.open('mailto:sales@sahicrm.com?subject=International Subscription Inquiry', '_blank')}
	//           className="w-full py-4 font-semibold text-lg sahi-primary-button mb-4"
	//         >
	//           Contact sales@sahicrm.com
	//         </Button>
	//       </div>
	//     </div>
	//   );

	return (
		<Dialog open={open} onOpenChange={handleOpenChange} preventOutsideClick={true}>
			<DialogContent className={`w-full ${filteredPlans.length === 1 ? 'sm:max-w-lg' : filteredPlans.length === 2 ? 'sm:max-w-4xl lg:max-w-5xl' : 'sm:max-w-6xl'}`}>
				<DialogHeader>
					{!isDesktop && !hideCloseButton &&
						<DialogClose onClick={onClose} className="p-2 -ml-2">
							<ArrowLeft className={`h-6 w-6 text-color`} />
							<span className="sr-only">Back</span>
						</DialogClose>}
					<DialogTitle className="font-semibold text-gray-900 flex items-center justify-between w-full">
						<span>{isIOSDevice() ? 'Manage Subscription' : 'Upgrade Subscription'}</span>
					</DialogTitle>
					{!isDesktop && hideCloseButton &&
						<button onClick={handleLogout} className="ml-2 text-sahi-red hover:text-sahi-red/80 transition-colors">
							<LogOut className={`h-5 w-5`} />
							<span className="text-sm font-medium"></span>
						</button>}
					<div className="flex items-center gap-2">
						{isDesktop && hideCloseButton &&
							<Button onClick={handleLogout} className="bg-sahi-red border-sahi-red hover:text-white hover:border-sahi-red hover:text-sahi-red">
								<LogOut className="h-4 w-4" />
								Log out
							</Button>}
						{isDesktop && !hideCloseButton &&
							<DialogClose
								className="h-9 w-9 p-0 text-white hover:bg-white/20 flex items-center justify-center"
								tabIndex={-1}
								onClick={handleClose}
							>
								<X className="h-5 w-5" />
								<span className="sr-only">Close</span>
							</DialogClose>}
					</div>
				</DialogHeader>

				<div className="lg:max-h-[70dvh] max-h-[90dvh] overflow-y-auto lg:p-4">
					{agencyData?.SubTillDT && agencyData?.SubTypeID == 2 && (
						<div className="flex justify-center mb-4">
							<div className="inline-flex items-center gap-1.5 px-3 py-2 text-sahi-red bg-sahi-red-light sahi-radius">
								<span className="text-sm">
									{new Date(agencyData.SubTillDT) < new Date() ? 'Your subscription expired on' : 'Your subscription expires on'}
								</span>
								<span className="text-sm font-bold ">
									{formatDateFull(agencyData.SubTillDT)}
								</span>
							</div>
						</div>
					)}
					{isIndianUser ? (
						<>
							{showMM && showYY ? (
								<div className="md:flex gap-4 sm:gap-6 justify-center mx-auto px-5 xs:px-7">
									{getFilteredPlans(subscriptionPlans, currEdnTID, agencyData)
										.filter(plan => {
											const isCurrentPlan = currEdnTID > 1 ? plan.editionID === currEdnTID : false;
											const isRenewalScenario = isCurrentPlan && plan.editionID > 1 && ((selectedPlan === 'yearly' && subsType === 'YY') || (selectedPlan === 'monthly' && subsType === 'MM'));
											return (plan?.show || isRenewalScenario || isCurrentPlan) && (plan.monthly || plan.yearly);
										})
										.map(renderPricingCard)}
								</div>
							) : showYY && !showMM ? (
								<>
									<div className={`md:flex gap-4 sm:gap-6 justify-items-center md:justify-center item mx-auto`}>
										{getFilteredPlans(subscriptionPlans, currEdnTID, agencyData)
											.filter(plan => {
												const isCurrentPlan = currEdnTID > 1 ? plan.editionID === currEdnTID : false;
												const isRenewalScenario = isCurrentPlan && plan.editionID > 1 && ((selectedPlan === 'yearly' && subsType === 'YY') || (selectedPlan === 'monthly' && subsType === 'MM'));
												return (plan?.show || isRenewalScenario || isCurrentPlan)  && (plan.yearly || plan.monthly);
											})
											.map(renderPlanCard)}
									</div>
								</>
							) : <></>
							}
						</>
					) : (
						<>
							<div className="flex items-center justify-center mb-6 sm:mb-8">
								<div className="bg-gray-100 sahi-radius flex">
								</div>
							</div>
							<div className="md:flex gap-4 sm:gap-6 justify-center mx-auto px-10">
								{getFilteredPlans(subscriptionPlans, currEdnTID, agencyData)
									.filter(plan => {
										const isCurrentPlan = currEdnTID > 1 ? plan.editionID === currEdnTID : false;
										const isRenewalScenario = isCurrentPlan && plan.editionID > 1 && ((selectedPlan === 'yearly' && subsType === 'YY') || (selectedPlan === 'monthly' && subsType === 'MM'));
										return (plan?.show || isRenewalScenario || isCurrentPlan) && (plan.yearly || plan.monthly);
									})
									.map(renderPricingCard)}
							</div>
						</>
					)}
				</div>

				<div className='text-center mb-4 text-xs'>
					<span><i>{isIndianUser ? '* Taxes extra. T&C apply.' : '* Annual subscription. T&C apply.'}</i></span>
				</div>

			</DialogContent>

			{showProfileDialog && (
				<ProfileSection
					open={showProfileDialog}
					onClose={handleProfileDialogClose}
					onSuccess={handleProfileSaveSuccess}
					requiredFields={true}
					isUpgradeFlow={changeType === 'SUB'}
				/>
			)}
			<StartSubscriptionDialog
				open={showStartSubscriptionDialog}
				onClose={handleStartSubscriptionClose}
				onPaymentClick={handleStartSubscriptionPayment}
				selectedPlan={selectedPlanForDialog}
				selectedBillingPeriod={pendingBillingPeriod}
				agencyData={agencyData}
				currencySymbol={currencySymbol}
				userData={userData}
				quantity={1}
				currentEditionID={currEdnTID}
				rfdAvl={rfdAvl}
				onPaymentStart={onPaymentStart}
				onPaymentCancel={onPaymentCancel}
				onPaymentSuccess={onPaymentSuccess}
			/>

			{showCouponModal && couponPlan && (
				<AvailableCoupons
					open={showCouponModal}
					editionID={couponPlan.editionID}
					billingPeriod={couponBillingPeriod}
					changeType={couponChangeType}
					onCouponSelect={handleCouponSelect}
					onClose={() => setShowCouponModal(false)}
				/>
			)}
		</Dialog>
	);
};

export default UpgradeSubscription;
